import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { MENU_ITEMS } from "../../../configs/menuItems";
import { useCommandSearch } from "../../../hooks/useCommandSearch";
import useEscape from "../../../hooks/useEscape";
import useKeyDown from "../../../hooks/useKeyDown";
import { useConsultants } from "../../../networking/consultant.hooks";
import { useCustomerUsers, useCustomers } from "../../../networking/customer.hooks";
import { useJobs } from "../../../networking/job.hooks";
import {
	consultantRoute,
	customerRoute,
	jobRoute,
	navigateToCustomerUser,
} from "../../../utils/route.utils";
import AppIcon from "../../Core/AppIcon/AppIcon";
import AppInput from "../../Core/AppInput/AppInput";
import AppText from "../../Core/AppText/AppText";
import Flex from "../../Layout/Flex";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import { useAuthStore } from "../../../stores/auth.store";

type SearchHitType = "consultant" | "customer" | "job" | "customer-user" | "command";
type SearchHit = { id: string; customerId?: string; route?: string; type: SearchHitType };

export interface GlobalSearchHandle {
	openSearch: () => void;
}

const GlobalSearch = forwardRef<GlobalSearchHandle>((props, ref) => {
	const navigate = useNavigate();
	const authStore = useAuthStore();

	const [showSearch, setShowSearch] = useState(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const [searchVal, setSearchVal] = useState("");
	const [focusedSearchHit, setFocusedSearchHit] = useState<SearchHit>();

	// Command search
	const commands = useCommandSearch(MENU_ITEMS, authStore.scopes, searchVal);

	const { jobList, isFetching: isLoadingJobs } = useJobs(
		{ page: 1, pageSize: 5, keyword: searchVal },
		{ enabled: !!searchVal && searchVal.length > 1 }
	);

	const { consultantList, isFetching: isLoadingConsultants } = useConsultants(
		{ page: 1, pageSize: 5, keyword: searchVal },
		!!searchVal && searchVal.length > 1
	);

	const { customers, isFetching: isLoadingCustomers } = useCustomers(
		{ page: 1, pageSize: 5, keyword: searchVal },
		!!searchVal && searchVal.length > 1
	);

	const {
		customerUsers,
		totalCount: customerUserTotalCount,
		isFetching: isLoadingCustomerUsers,
	} = useCustomerUsers(
		{
			page: 1,
			pageSize: 5,
			keyword: searchVal,
		},
		!!searchVal && searchVal.length > 1
	);

	const searchHits = useMemo<SearchHit[]>(() => {
		return [
			// Commands first
			...commands.map((command) => ({
				id: command.id,
				type: "command" as SearchHitType,
				route: command.route,
			})),
			// Then data results
			...(jobList?.jobs || []).map(({ id }) => ({ id, type: "job" as SearchHitType })),
			...(consultantList?.users || []).map(({ id }) => ({
				id,
				type: "consultant" as SearchHitType,
			})),
			...(customers?.customers || []).map(({ id }) => ({
				id,
				type: "customer" as SearchHitType,
			})),
			...(customerUsers || []).map(({ id, profile }) => ({
				id,
				type: "customer-user" as SearchHitType,
				customerId: (profile?.customers || [])[0].id,
			})),
		];
	}, [commands, jobList?.jobs, customers?.customers, consultantList?.users, customerUsers]);

	function openSearch() {
		setShowSearch(true);
		setSearchVal("");

		setTimeout(() => {
			searchInputRef.current?.focus();
		}, 10);
	}

	useImperativeHandle(ref, () => ({
		openSearch,
	}));

	useKeyDown({
		onKeyDown: (e) => {
			if (e.shiftKey && (e.key === "S" || e.key === "s")) {
				openSearch();
			}

			if (showSearch) {
				const focusedIndex = searchHits.findIndex((item) => item.id === focusedSearchHit?.id);

				if (!searchHits.find((hit) => hit.id === focusedSearchHit?.id)) {
					// Clear focused search hit in case it is no longer amongst search hits
					setFocusedSearchHit(undefined);
				}

				if (e.key === "ArrowDown") {
					setFocusedSearchHit(searchHits[Math.min(focusedIndex + 1, searchHits.length - 1)]);
				} else if (e.key === "ArrowUp") {
					setFocusedSearchHit(searchHits[Math.max(focusedIndex - 1, 0)]);
				} else if (e.key === "Enter") {
					e.stopPropagation();
					const theFocusedSearchHit = searchHits.length === 1 ? searchHits[0] : focusedSearchHit;

					if (!theFocusedSearchHit) return;

					updateFocusedSearchHit(theFocusedSearchHit, true);
				}
			}
		},
		disableIfAnyInputHasFocus: !showSearch,
	});

	function updateFocusedSearchHit(searchHit: SearchHit, navigateToIt?: boolean) {
		setFocusedSearchHit(searchHit);

		if (navigateToIt) {
			setTimeout(() => {
				if (searchHit.type === "command" && searchHit.route) {
					navigate(searchHit.route);
				} else if (searchHit.type === "consultant") {
					navigate(consultantRoute(searchHit.id));
				} else if (searchHit.type === "customer") {
					navigate(customerRoute(searchHit.id));
				} else if (searchHit.type === "job") {
					navigate(jobRoute(searchHit.id));
				} else if (searchHit.type === "customer-user" && searchHit.customerId) {
					navigateToCustomerUser(navigate, searchHit.customerId, searchHit.id);
				}
				setShowSearch(false);
			}, 100);
		}
	}

	useEscape({ onEscape: () => setShowSearch(false), enabled: showSearch });

	if (!showSearch) {
		return null;
	}

	return (
		<Styles onClick={() => setShowSearch(false)}>
			<div className="global-search" onClick={(e) => e.stopPropagation()}>
				<div className="space-y-3">
					<div>
						<AppInput
							icon="search"
							ref={searchInputRef}
							placeholder="Sök genvägar, uppdrag, konsulter (även personnr), kunder"
							margin={"0"}
							containerMargin="0 0 0.5rem 0"
							onChangeText={setSearchVal}
							value={searchVal}
						/>
						<AppText color="grayTextLight" size="s">
							Sök genvägar eller ange fritext på exempelvis namn, uppdrags-, kund- eller
							anställningsnummer
							<br />
						</AppText>
						{/* <AppText color="grayTextLight" size="xs">
							Snabböppna sök med <strong>shift + s</strong>
						</AppText> */}
					</div>

					<div className="space-y-4 max-h-[40vh] overflow-y-scroll">
						{commands.length > 0 && (
							<Flex direction="column" gap="0.25rem">
								<AppText weight="medium" margin="0 0 3px 0">
									Genvägar (visar {commands.length} st)
								</AppText>
								{commands.map((command) => (
									<Flex
										key={command.id}
										border="1px solid #ddd"
										padding="5px"
										borderRadius="5px"
										gap="10px"
										cursor="pointer"
										onClick={() =>
											updateFocusedSearchHit(
												{ id: command.id, type: "command", route: command.route },
												true
											)
										}
										className={`search-hit-box ${command.id === focusedSearchHit?.id && "focused"}`}
										alignItems="center"
									>
										<div className="command-icon">
											<AppIcon icon={command.icon} size="16px" />
										</div>
										<AppText size="s">{command.translatedTitle}</AppText>
										{command.parentTitle && (
											<AppText size="s" color="grayTextDark">
												({command.parentTitle})
											</AppText>
										)}
									</Flex>
								))}
							</Flex>
						)}

						{jobList?.jobs && (
							<Flex direction="column" gap="0.25rem">
								<AppText weight="medium" margin="0 0 3px 0">
									Uppdrag (visar {jobList.jobs.length} av {jobList.totalCount} st)
								</AppText>
								{jobList.jobs.map((job) => (
									<Flex
										key={job.id}
										border="1px solid #ddd"
										padding="5px"
										borderRadius="5px"
										gap="10px"
										cursor="pointer"
										onClick={() => updateFocusedSearchHit({ id: job.id, type: "job" }, true)}
										className={`search-hit-box ${job.id == focusedSearchHit?.id && "focused"}`}
										data-job={job.id}
									>
										<AppText size="s" color="grayTextDark">
											#{job.orderNumber}
										</AppText>
										<AppText size="s">{job.customer.name}</AppText>
									</Flex>
								))}
							</Flex>
						)}

						{consultantList?.users && (
							<Flex direction="column" gap="0.25rem">
								<AppText weight="medium" margin="0 0 3px 0">
									Konsulter (visar {consultantList.users.length} av {consultantList.totalCount} st)
								</AppText>
								{consultantList.users.map((consultant) => (
									<Flex
										key={consultant.id}
										border="1px solid #ddd"
										padding="5px"
										borderRadius="5px"
										gap="10px"
										cursor="pointer"
										onClick={() =>
											updateFocusedSearchHit({ id: consultant.id, type: "consultant" }, true)
										}
										className={`search-hit-box ${
											consultant.id == focusedSearchHit?.id && "focused"
										}`}
										data-contact={consultant.id}
									>
										<AppText size="s" color="grayTextDark">
											#{consultant.profile.employeeNumber}
										</AppText>
										<AppText size="s">
											{consultant.firstName} {consultant.lastName}
										</AppText>
									</Flex>
								))}
							</Flex>
						)}

						{customers?.customers && (
							<Flex direction="column" gap="0.25rem">
								<AppText weight="medium" margin="0 0 3px 0">
									Kunder (visar {customers.customers.length} av {customers.totalCount} st)
								</AppText>
								{customers.customers.map((customer) => (
									<Flex
										key={customer.id}
										border="1px solid #ddd"
										padding="5px"
										borderRadius="5px"
										gap="10px"
										cursor="pointer"
										onClick={() =>
											updateFocusedSearchHit({ id: customer.id, type: "customer" }, true)
										}
										className={`search-hit-box ${customer.id == focusedSearchHit?.id && "focused"}`}
										data-customer={customer.id}
									>
										<AppText size="s" color="grayTextDark">
											#{customer.customerNumber}
										</AppText>
										<AppText size="s">{customer.name}</AppText>
									</Flex>
								))}
							</Flex>
						)}

						{customerUsers && (
							<Flex direction="column" gap="0.25rem">
								<AppText weight="medium" margin="0 0 3px 0">
									Kundanvändare (visar {customerUsers.length} av {customerUserTotalCount} st)
								</AppText>
								{customerUsers.map((customerUser) => (
									<Flex
										key={customerUser.id}
										border="1px solid #ddd"
										padding="5px"
										borderRadius="5px"
										gap="10px"
										cursor="pointer"
										onClick={() =>
											updateFocusedSearchHit(
												{
													id: customerUser.id,
													customerId: (customerUser.profile?.customers || [])[0].id,
													type: "customer-user",
												},
												true
											)
										}
										data-contact={customerUser.id}
										className={`search-hit-box ${
											customerUser.id == focusedSearchHit?.id && "focused"
										}`}
									>
										<AppText size="s" color="grayTextDark">
											#{customerUser.profile?.phoneNumber}
										</AppText>
										<AppText size="s">
											{customerUser.firstName} {customerUser.lastName}
										</AppText>
									</Flex>
								))}
							</Flex>
						)}
					</div>

					{(isLoadingJobs ||
						isLoadingConsultants ||
						isLoadingCustomers ||
						isLoadingCustomerUsers) && <LoadingIndicator showText={false} />}
				</div>
			</div>
		</Styles>
	);
});

GlobalSearch.displayName = "GlobalSearch";

export default GlobalSearch;

const Styles = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background-color: rgb(0, 0, 0, 0.15);
	z-index: 100;

	.global-search {
		position: absolute;
		padding: 1rem;
		left: calc(50vw - 300px + 50px);
		top: 100px;
		width: 600px;
		min-height: 100px;
		background: white;
		border: 1px solid ${(props) => props.theme.colors.INPUT_BORDER_FOCUSED};
		border-radius: 15px;
		box-shadow: 0px 3px 30px rgba(0, 0, 0, 0.3);

		.search-hit-box {
			&:hover {
				background-color: #f9f9f9;
			}

			&.focused {
				background-color: ${(props) => props.theme.colors.COLOR_LIGHT_BLUE2};
				border-color: ${(props) => props.theme.colors.COLOR_ACTION_BLUE};
			}
		}

		.command-icon {
			filter: invert(1) grayscale(1) brightness(0.4);
			display: flex;
			align-items: center;
		}
	}
`;
